/**
 * Client-side mirror of backend OrderEditService.canEditOrder. Used to
 * decide whether to render edit buttons as enabled or disabled (with a
 * human-readable reason for the disabled state).
 *
 * Backend is authoritative — UI just hides obvious dead ends. Buttons
 * still gated by the API on submit.
 */

export type OrderEditOp =
  | 'address'
  | 'add_items'
  | 'remove_items';

export interface EditabilityInput {
  status: string;
  pharmacyOrderId?: string | null;
  lastPharmacyStatus?: string;
  physicianId: string;
  createdById?: string;       // BulkOrder only
  orderType?: 'order' | 'bulk_order';
  pendingAddressChange?: { state?: string } | null;
}

export interface EditabilityUser {
  _id: string;
  role: string;
}

export interface EditabilityResult {
  ok: boolean;
  reason?: string;
}

const TERMINAL_REGULAR = new Set(['voided', 'cancelled', 'failed']);
const TERMINAL_BULK = new Set(['voided', 'cancelled']);

const DIRECT_ADDRESS_STATUSES = new Set([
  'TO_BE_FILLED',
  'PARTIAL_FILL',
  'COMPLETE_FILLED',
  'ON_HOLD',
  'PULLED'
]);
const FORCE_ADDRESS_STATUSES = new Set(['LABEL_CREATED', 'ISSUE_IN_SHIPPING']);
const ITEM_BLOCKED = new Set([
  'COMPLETE_FILLED', 'LABEL_CREATED', 'ISSUE_IN_SHIPPING',
  'IN_SHIPPING', 'OUT_FOR_DELIVERY', 'DELIVERED', 'PARTIAL_COMPLETED',
  'SHIPPED_TO_CLINIC', 'CANCELLED', 'CLAIM_FILED', 'CLAIM_RESHIPPED',
  'COMPLETED', 'PICKED_UP_BY_PATIENT'
]);
const ITEM_REMOVE_BLOCKED = new Set([
  'PARTIAL_FILL',
  ...ITEM_BLOCKED
]);

function isAdmin(user: EditabilityUser): boolean {
  return user.role === 'admin';
}

function isOwnerPhysician(order: EditabilityInput, user: EditabilityUser): boolean {
  return user.role === 'physician' && order.physicianId === user._id;
}

function isOwnerCreator(order: EditabilityInput, user: EditabilityUser): boolean {
  return order.orderType === 'bulk_order' && order.createdById === user._id;
}

function statusIsTerminal(order: EditabilityInput): boolean {
  if (order.orderType === 'bulk_order') return TERMINAL_BULK.has(order.status);
  return TERMINAL_REGULAR.has(order.status);
}

function preSubmissionToPharmacy(order: EditabilityInput): boolean {
  return !order.pharmacyOrderId;
}

export function canEditOrderClient(
  order: EditabilityInput,
  user: EditabilityUser,
  op: OrderEditOp
): EditabilityResult {
  if (statusIsTerminal(order)) {
    return { ok: false, reason: `Order is ${order.status}; edits not allowed.` };
  }

  const admin = isAdmin(user);
  const physicianOwner = isOwnerPhysician(order, user);
  const bulkCreator = isOwnerCreator(order, user);

  if (!admin && !physicianOwner && !bulkCreator) {
    return { ok: false, reason: 'You are not authorized to edit this order.' };
  }

  // Remove items + qty change are regular-order only. Add-items now works
  // for both regular orders and bulks (bulk additions spawn a child bulk).
  if (op === 'remove_items' && order.orderType === 'bulk_order') {
    return { ok: false, reason: 'Removing items from a bulk is not supported. Cancel the bulk instead.' };
  }

  if (order.pendingAddressChange?.state === 'pending' && op === 'address') {
    return { ok: false, reason: 'An address change is already pending pharmacy review.' };
  }

  const ph = order.lastPharmacyStatus;
  const preSubmit = preSubmissionToPharmacy(order);

  if (op === 'address') {
    if (preSubmit) return { ok: true };
    if (ph && DIRECT_ADDRESS_STATUSES.has(ph)) return { ok: true };
    if (ph && FORCE_ADDRESS_STATUSES.has(ph)) {
      if (admin) {
        return { ok: true, reason: 'Shipping label is already created — change will go through pharmacy review.' };
      }
      return { ok: false, reason: 'Shipping label is already created. An admin must complete this change.' };
    }
    return { ok: false, reason: `Address can't be changed — package status is ${humanizeStatus(ph)}.` };
  }

  if (op === 'add_items') {
    if (preSubmit) return { ok: true };
    if (ph && !ITEM_BLOCKED.has(ph)) return { ok: true };
    return { ok: false, reason: `Items can't be added — pharmacy has already finished filling (${humanizeStatus(ph)}).` };
  }

  if (op === 'remove_items') {
    if (preSubmit) return { ok: true };
    if (ph && !ITEM_REMOVE_BLOCKED.has(ph)) return { ok: true };
    return { ok: false, reason: `Items can't be removed — pharmacy has started filling (${humanizeStatus(ph)}).` };
  }

  return { ok: false, reason: 'Unsupported edit.' };
}

function humanizeStatus(s: string | undefined): string {
  if (!s) return 'unknown';
  return s.toLowerCase().replace(/_/g, ' ');
}
